import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';

import { DataAPIContext, GET_LIST } from '../api';
import { ContextStore } from '../store/contextStore';
import { debounce } from '../utils';
import FormLicenseeContext from './FormLicenseeContext';

const useStyles = makeStyles((theme) => ({
    // Options styling
    menuList: {
        maxHeight: '250px !important',
        '& li.Mui-focused': {},
        '& li': {},
    },
}));

// How close (px) to the end of the list a scroll has to get before the next page loads.
const LOAD_MORE_THRESHOLD_PX = 48;

// The options list with a "Loading more…" row under it while the next page is on its way.
const PagedListbox = React.forwardRef(({ loadingMore, children, ...rest }, ref) => (
    <ul ref={ref} {...rest}>
        {children}
        {loadingMore
            ? <Box component="li" role="presentation" sx={{ px: 2, py: 0.75, color: 'text.secondary', typography: 'body2' }}>Loading more…</Box>
            : null}
    </ul>
));

const appendNewOptions = (current, page) => {
    const seen = new Set(current.map((o) => o?.id).filter((id) => id != null));
    return [...current, ...page.filter((o) => o?.id == null || !seen.has(o.id))];
};

// `pageSize` turns on paging: the field loads that many options, and the next page once the list
// is scrolled to its end. Without it a field shows its first page only (the data provider's 25
// unless `optionsResultsSize` says otherwise) and the rest can only be reached by typing (CP-6056).
export const RemoteAutocomplete = React.forwardRef((props, ref) => {
    const dataAPI = React.useContext(DataAPIContext);
    const formLicensee = React.useContext(FormLicenseeContext);
    const { isFranchise } = React.useContext(ContextStore);

    const isMountedRef = React.useRef(true);
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [open, setOpen] = React.useState(false);
    const [staleOptions, setStaleOptions] = React.useState(true);
    const [options, setOptions] = React.useState([]);
    const [loadedEmptyOptions, setLoadedEmptyOptions] = React.useState(false);
    const loading = open && (staleOptions || (options.length <= 0 && !loadedEmptyOptions));
    const [query, setQuery] = React.useState(null);
    // Entries are { options, nextPage, hasMore }: a paged field keeps the pages it already loaded.
    const cacheRef = React.useRef(new Map());
    const pageSize = props.pageSize || null;
    const [loadingMore, setLoadingMore] = React.useState(false);
    const loadingMoreRef = React.useRef(false);
    const optionsRef = React.useRef([]);
    const nextPageRef = React.useRef(2);
    const hasMoreRef = React.useRef(false);
    // Bumped by every search, so an answer that a newer search has overtaken is dropped instead of
    // replacing the newer one's options (the Pickup tags still listed after the search was cleared).
    const requestSeqRef = React.useRef(0);
    const listboxRef = React.useRef(null);
    const restoreScrollTopRef = React.useRef(null);

    // NEW: allow controlled input, fall back to local state
    const [localInput, setLocalInput] = React.useState('');
    const effectiveInput = props.inputValue ?? localInput;
    const [isFocused, setIsFocused] = React.useState(false);

    const inputRef = React.useRef(null);
    const urlSuffix = props.urlSuffix === void 0 ? 'autocomplete/' : props.urlSuffix;
    const classes = useStyles();

    const sanitize = React.useCallback((val) => {
        let searchVal = val ?? '';
        for (let i = 0; i < (props.searchCharsToIgnore?.length || 0); i++) {
            const ch = props.searchCharsToIgnore[i];
            searchVal = searchVal.split(ch).join('');
        }
        return searchVal;
    }, [props.searchCharsToIgnore]);

    const getFilterQuery = React.useCallback(() => {
        const rawQ = (props.query !== undefined ? props.query : query) ?? '';
        const s = sanitize(rawQ);
        return props.normalizeQuery ? props.normalizeQuery(s) : s;
    }, [props.query, query, sanitize, props.normalizeQuery]);

    const makeCacheKey = React.useCallback(() => {
        const keyParts = {
            resource: props.resource,
            urlSuffix,
            query: getFilterQuery(),
            apiParams: props.apiParams || {},
            apiFilters: props.apiFilters || {},
            resultsSize: props.optionsResultsSize || '',
            pageSize: pageSize || '',
            formLicensee: formLicensee || '',
        };
        return JSON.stringify(keyParts);
    }, [props.resource, urlSuffix, getFilterQuery, props.apiParams, props.apiFilters, props.optionsResultsSize, pageSize, formLicensee]);
    const latestCacheKeyRef = React.useRef(null);
    latestCacheKeyRef.current = makeCacheKey();

    const buildParams = (page) => {
        const filterQuery = getFilterQuery();
        const params = {
            ...props.params,
            convert: props.convert,
            filter: { query: filterQuery, filters: undefined },
            urlSuffix: urlSuffix,
        };
        if (formLicensee) {
            params.filter = { ...params.filter, franchise_to: formLicensee };
        }
        if (props.apiParams) {
            params.filter = { ...params.filter, ...props.apiParams };
        }
        if (props.apiFilters) {
            params.filter.filters = JSON.stringify(props.apiFilters);
        }

        if (pageSize) {
            params.pagination = { page, perPage: pageSize };
        } else if (props.optionsResultsSize) {
            params.pagination = { page: 1, perPage: props.optionsResultsSize };
        }
        return params;
    };

    const showEntry = (entry) => {
        optionsRef.current = entry.options;
        nextPageRef.current = entry.nextPage;
        hasMoreRef.current = entry.hasMore;
        setOptions(entry.options);
        props.setOptions?.(entry.options);
        props.setOptionsCount?.(entry.options.length);
    };

    const applyEntry = (entry) => {
        showEntry(entry);
        setStaleOptions(false);
        setLoadedEmptyOptions(entry.options.length <= 0);
    };

    const refreshResults = async (active) => {
        const seq = ++requestSeqRef.current;
        const cacheKey = makeCacheKey();
        if (!props.disableCache && cacheRef.current.has(cacheKey)) {
            if (active && isMountedRef.current) {
                applyEntry(cacheRef.current.get(cacheKey));
            }
            return;
        }

        const res = await dataAPI(GET_LIST, props.resource, buildParams(1));

        if (active && isMountedRef.current && seq === requestSeqRef.current) {
            // Cache the mapped options (not the raw response): the cache-hit
            // path above applies them directly without re-running mapResults,
            // so storing raw data here would render reshaped options (e.g. the
            // departure dropdown) blank on subsequent opens.
            const entry = {
                options: props.mapResults ? props.mapResults(res.data, res) : res.data,
                nextPage: 2,
                hasMore: !!(pageSize && res.next),
            };
            applyEntry(entry);
            if (!props.disableCache) {
                cacheRef.current.set(cacheKey, entry);
            }
        }
    };

    const loadMore = async () => {
        if (!pageSize || !hasMoreRef.current || loadingMoreRef.current || staleOptions) {
            return;
        }
        const seq = requestSeqRef.current;
        const cacheKey = makeCacheKey();
        const page = nextPageRef.current;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const res = await dataAPI(GET_LIST, props.resource, buildParams(page));
            // The search changed while the page was on its way (typed text, filters): it belongs
            // to a list that is no longer shown.
            if (!isMountedRef.current || seq !== requestSeqRef.current || cacheKey !== latestCacheKeyRef.current) {
                return;
            }
            const pageOptions = (props.mapResults ? props.mapResults(res.data, res) : res.data) || [];
            const entry = {
                options: appendNewOptions(optionsRef.current, pageOptions),
                nextPage: page + 1,
                hasMore: !!res.next && pageOptions.length > 0,
            };
            // MUI scrolls the list back to the top when the option count changes while nothing is
            // highlighted (a scrollbar drag); put it back where the user was once it has.
            restoreScrollTopRef.current = listboxRef.current ? listboxRef.current.scrollTop : null;
            showEntry(entry);
            if (!props.disableCache) {
                cacheRef.current.set(cacheKey, entry);
            }
        } finally {
            loadingMoreRef.current = false;
            if (isMountedRef.current) {
                setLoadingMore(false);
            }
        }
    };

    const onListboxScroll = (ev) => {
        const el = ev.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD_PX) {
            loadMore();
        }
    };

    // The "Loading more…" row is added under the end of the list, which is where the user is:
    // keep it in view.
    React.useEffect(() => {
        if (loadingMore && listboxRef.current) {
            listboxRef.current.scrollTop = listboxRef.current.scrollHeight;
        }
    }, [loadingMore]);

    // Runs after the Autocomplete's own effects (a parent's effects follow its children's), so
    // the scroll position is restored after MUI has reset it.
    React.useEffect(() => {
        const el = listboxRef.current;
        if (restoreScrollTopRef.current != null && el) {
            el.scrollTop = restoreScrollTopRef.current;
        }
        restoreScrollTopRef.current = null;
        // A page that doesn't fill the list (most of it already selected, say) leaves nothing to
        // scroll, so nothing would ever ask for the next one.
        if (open && el && hasMoreRef.current && el.scrollHeight <= el.clientHeight + LOAD_MORE_THRESHOLD_PX) {
            loadMore();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, open]);

    React.useEffect(() => {
        // Before the timer: started first, it outlived the early return uncleared and fetched
        // again 500 ms after every load (and after unmount).
        if (!loading) {
            return undefined;
        }
        let active = true;
        const tm = setTimeout(async () => {
            refreshResults(active);
        }, 500);
        setLoadedEmptyOptions(false);

        return () => {
            active = false;
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, query]);

    React.useEffect(() => {
        // deferFetchUntilOpen: don't hit the backend on mount / refreshKey
        // change while the dropdown is closed — mark the options stale instead,
        // and onOpen's staleOptions → loading path fetches on first open. For
        // expensive lookups (Test Render "recent deliveries" walks daily log
        // partitions) this turns one query per dialog open + one per
        // refreshKey change into one query per actual open.
        if (props.deferFetchUntilOpen && !open) {
            setStaleOptions(true);
            return;
        }
        refreshResults(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey]);

    React.useEffect(() => {
        cacheRef.current.clear();
    }, [props.refreshKey]);

    // Re-fetch when the form-level licensee changes
    React.useEffect(() => {
        if (formLicensee != null) {
            cacheRef.current.clear();
            setStaleOptions(true);
        }
    }, [formLicensee]);

    // NEW: drive fetching from Autocomplete input changes (debounced)
    const debouncedSetQuery = React.useMemo(() =>
        debounce((val) => {
            const s = sanitize(val);
            setStaleOptions(true);
            setQuery(s);
            props.onQueryChange?.(s); // keep backwards compatibility
            setLoadedEmptyOptions(false);
        }, 300),
        [sanitize, props]);

    React.useEffect(() => {
        return () => {
            debouncedSetQuery.cancel?.();
        };
    }, [debouncedSetQuery]);

    // Build Autocomplete props
    const acProps = {};
    if (props.multiple) acProps.multiple = true;
    if (props.disabled) acProps.disabled = true;
    if (props.hasOwnProperty('value')) acProps.value = props.value;
    if (props.hasOwnProperty('defaultValue')) acProps.defaultValue = props.defaultValue;
    if (props.style) acProps.style = props.style;
    if (props.width) acProps.style = Object.assign({}, props.style, { width: `${props.width}px` });
    if (props.fullWidth) acProps.fullWidth = true;
    if (props.renderTags) acProps.renderTags = props.renderTags;

    const textFieldProps = { required: props.required, ...props.textFieldProps };

    const onChange = React.useCallback((ev, nv, reason) => {
        if (reason === 'clear') {
            setQuery(null);
            setLocalInput('');
        }
        props.onChange && props.onChange(ev, nv, reason);
    }, [props.onChange]);

    const onFocus = () => {
        setIsFocused(true);
        props.onFocus && props.onFocus();
    };

    const onBlur = () => {
        setIsFocused(false);
        if (props.resetQueryOnBlur) {
            setQuery('');
            setLocalInput('');
        }
        props.onBlur && props.onBlur();
    };

    const optionsIncludingValue = React.useMemo(() => {
        const vals = props.value && props.value.constructor === Array ? props.value : props.value ? [props.value] : [];
        const out = [...options];
        for (const v of vals) {
            if (v && typeof v === 'object' && v.id != null && !out.some((o) => o && o.id == v.id)) {
                out.unshift(v);
            }
        }
        return out;
    }, [options, props.value]);

    const baseGetOptionLabel = props.getOptionLabel || ((option) => (option && option.name ? option.name : ''));
    const getOptionLabel = isFranchise
        ? (option) => {
            const label = baseGetOptionLabel(option);
            const licensee = option?.tour_operator?.name;
            return licensee ? `${label} [${licensee}]` : label;
        }
        : baseGetOptionLabel;

    const defaultIsOptionEqualToValue = React.useCallback((option, value) => {
        if (option === value) return true;
        if (option == null || value == null) return option == value;
        if (typeof value === 'number' || typeof value === 'string') {
            return value == option?.id;
        }
        if (typeof option === 'object' && typeof value === 'object' && option.id != null && value.id != null) {
            return option.id == value.id;
        }
        return false;
    }, []);

    // Keep the displayed text in sync when `value` changes for a reason other than
    // the user typing (e.g. the parent replaces its data after a save completes -
    // CP-7186). Skipped while focused so we never clobber active typing, and
    // skipped for usages where display text isn't meant to track `value` 1:1
    // (parent-controlled inputValue, multi-select chips, free-text filter/search
    // fields with clearOnBlur={false} or resetQueryOnBlur).
    React.useEffect(() => {
        if (
            props.inputValue !== undefined
            || props.multiple
            || isFocused
            || props.clearOnBlur === false
            || props.resetQueryOnBlur
        ) {
            return;
        }
        const label = props.value == null ? '' : (getOptionLabel(props.value) || '');
        setLocalInput((prev) => (prev === label ? prev : label));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, isFocused, props.inputValue, props.multiple, props.clearOnBlur, props.resetQueryOnBlur]);

    return (
        <Autocomplete
            ref={ref}
            {...acProps}
            open={open}
            onOpen={() => {
                setOpen(true);
                setLoadedEmptyOptions(false);
                setStaleOptions(true);
                debouncedSetQuery.cancel?.();
                // For single-select dropdowns with a selected value, the input still
                // contains the value's label after the previous selection. Filtering by
                // that label on re-open hides every other option. resetQueryOnOpen lets
                // the consumer opt into "always show the full list on open"; the user
                // can then narrow by typing.
                if (props.resetQueryOnOpen) {
                    if (props.inputValue === undefined) setLocalInput('');
                    setQuery('');
                } else {
                    const s = sanitize(effectiveInput);
                    setQuery(s);
                }
            }}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={props.isOptionEqualToValue || props.getOptionSelected || defaultIsOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            renderOption={props.renderOption}
            noOptionsText={props.noOptionsText || 'No matches'}
            options={optionsIncludingValue}
            filterSelectedOptions={!!props.filterSelected}
            filterOptions={props.filterOptions}
            loading={loading}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disableClearable={props.disableClearable}
            classes={{ paper: classes.paper, option: classes.option, listbox: classes.listbox }}
            clearOnBlur={props.clearOnBlur !== false}
            ListboxComponent={pageSize ? PagedListbox : undefined}
            ListboxProps={pageSize
                ? { className: classes.menuList, ref: listboxRef, onScroll: onListboxScroll, loadingMore }
                : { className: classes.menuList }}

            /* controlled input support */
            inputValue={effectiveInput}
            onInputChange={(e, value, reason) => {
                // keep local input in sync if parent is not controlling
                if (props.inputValue === undefined) setLocalInput(value);
                // bubble to parent if they want to listen
                props.onInputChange?.(e, value, reason);
                // drive remote query (debounced)
                debouncedSetQuery(value);
            }}

            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    label={props.label}
                    variant={props.variant}
                    size={props.size}
                    inputRef={inputRef}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                    error={props.error}
                />
            )}
            onKeyDown={props.onKeyDown}
        />
    );
});

export default RemoteAutocomplete;
